/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / DEALS-GUEST — Deal-Kachel + geführter Zusammenstell-Flow (S169 P3)
   =============================================================================
   §5b: jeder aktive Deal erscheint als EINE prominente Kachel in der Speisekarte
   (Gast-Menü UND Cockpit-Order-Entry) — keine volle Rubrik, kein menu_categories-
   Eintrag. Klick → geführtes Modal (Gericht → Topping/Brot → Individualisieren →
   optional Beilage/Drink) → EINE Menü-Position.

   Sichtbarkeit:
   - Gast: active_deals_for_guest(restaurant_id, now) → schon Fenster-gefiltert.
   - Cockpit/Operator: deals_overview() + isDealAvailable(…, {isOperator:true})
     (Fenster ignoriert bei guest_only_window) → deal_detail(id) je sichtbarem Deal.

   DECOMPOSITION (§2): „Übernehmen" baut über den Pure-Core-Resolver
   (deals.js resolveDealToOrderItems) die order_items-Drafts (je Komponente
   expliziter Deal-Preis + eigener tax_rate, customizations.deal.line_uid) und
   übergibt sie als EINE Deal-Position an den Menü-Picker (addDealPosition).
   Der LIVE-Bestellpfad (order_items schreiben + KDS/Theke-Routing + Bon) ist P4.

   Optik: das Modal hängt im Menü-Root (helle Papier-Insel in Gast UND Cockpit-
   Order-Entry, S168). CSP: Inline-Handler nur func() (dom-bindings).
============================================================================ */

import { htmlEscape, fmtEUR, uid, showToast } from '../utils/helpers.js';
import { isDealAvailable } from '../core/deals.js';

/* -------------------------------------------------------------------------- */
/* Pure-Helfer (Kachel-Texte) — Vitest                                         */
/* -------------------------------------------------------------------------- */

const _DOW = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Kurz-Zusammenfassung der Gruppenpreise: „Burger & Salat 10 € · Bowls 12 €".
 *  Gruppen mit gleichem Preis werden zusammengefasst (Namen mit „&"). */
export function dealPriceSummary(deal) {
  const groups = (deal && deal.groups) || [];
  if (!groups.length) return '';
  const byPrice = new Map();
  groups.forEach(g => {
    const cents = Number(g.deal_price_cents ?? g.dealPriceCents ?? 0);
    if (!byPrice.has(cents)) byPrice.set(cents, []);
    byPrice.get(cents).push(g.name || '');
  });
  return Array.from(byPrice.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([cents, names]) => `${names.filter(Boolean).join(' & ')} ${fmtEUR(cents / 100)}`)
    .join(' · ');
}

/** Verfügbarkeits-Badge: „Mo–Fr 11:30–14:00" / „Mo·Mi·Fr 12:00–14:00" / „". */
export function dealWindowBadge(deal) {
  if (!deal) return '';
  const days = deal.available_days ?? deal.availableDays ?? null;
  const from = (deal.available_from ?? deal.availableFrom ?? '') || '';
  const to = (deal.available_to ?? deal.availableTo ?? '') || '';
  let dayStr = '';
  if (Array.isArray(days) && days.length) {
    const s = days.slice().sort((a, b) => a - b);
    // zusammenhängende Spanne → „Mo–Fr", sonst Punkt-Liste.
    const contiguous = s.every((n, i) => i === 0 || n === s[i - 1] + 1);
    dayStr = (contiguous && s.length > 2) ? `${_DOW[s[0]]}–${_DOW[s[s.length - 1]]}` : s.map(n => _DOW[n]).join('·');
  }
  const timeStr = (from || to) ? `${String(from).slice(0, 5) || '00:00'}–${String(to).slice(0, 5) || '24:00'}` : '';
  return [dayStr, timeStr].filter(Boolean).join(' ');
}

/* -------------------------------------------------------------------------- */
/* Infrastruktur                                                               */
/* -------------------------------------------------------------------------- */

let _deals = [];          // sichtbare Deals (voll verschachtelt)
let _isOperator = false;
let _sel = null;          // aktuelle Zusammenstellung (Modal-State)
let _root = null;         // AKTIVES Menü-Root-ELEMENT (Kacheln + Modal scoped, kein
                          // globales getElementById → kein Host-Duplikat-Bug bei
                          // mehreren Menü-Mounts: Reservierung/Takeaway/Lieferung/Cockpit)

function _supa() { return (window.App && window.App.supabase) || null; }
function _rid() {
  const a = window.App && window.App.auth;
  return (a && typeof a.getCurrentRestaurantIdSync === 'function' && a.getCurrentRestaurantIdSync())
    || (window.AppConfig && window.AppConfig.defaultRestaurantId) || null;
}
function _menu() {
  try {
    const st = window.App && window.App.storage && window.App.storage._getInternalState && window.App.storage._getInternalState();
    if (st && Array.isArray(st.menu)) return st.menu;
  } catch (_) { /* ignore */ }
  return [];
}
function _mi(id) { return _menu().find(m => m.id === id) || null; }

/* -------------------------------------------------------------------------- */
/* Laden                                                                       */
/* -------------------------------------------------------------------------- */

export async function loadVisibleDeals({ isOperator } = {}) {
  const supa = _supa(); const rid = _rid();
  if (!supa || !rid) return [];
  const now = new Date();
  if (isOperator) {
    // Cockpit: alle aktiven Deals, Fenster ignoriert (guest_only_window).
    try {
      const { data, error } = await supa.rpc('deals_overview');
      if (error) throw error;
      const visible = (Array.isArray(data) ? data : []).filter(d => isDealAvailable(d, now, { isOperator: true }));
      const details = await Promise.all(visible.map(d => supa.rpc('deal_detail', { p_deal_id: d.id })
        .then(r => r.error ? null : r.data)));
      return details.filter(Boolean);
    } catch (e) { console.warn('[deals-guest] operator load:', e && e.message); return []; }
  }
  // Gast: schon Fenster-gefiltert (anon-tauglich).
  try {
    const { data, error } = await supa.rpc('active_deals_for_guest', { p_restaurant_id: rid, p_now: now.toISOString() });
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  } catch (e) { console.warn('[deals-guest] guest load:', e && e.message); return []; }
}

/* -------------------------------------------------------------------------- */
/* Kacheln                                                                     */
/* -------------------------------------------------------------------------- */

export function renderDealTilesHtml(deals) {
  if (!deals || !deals.length) return '';
  const tiles = deals.map(d => {
    const badge = dealWindowBadge(d);
    return `
      <button type="button" class="dg-tile" onclick="dealsGuestOpen('${htmlEscape(d.id)}')">
        <span class="dg-tile-main">
          <span class="dg-tile-ribbon">Aktion</span>
          <span class="dg-tile-title">${htmlEscape(d.name)}</span>
          <span class="dg-tile-sum">${htmlEscape(dealPriceSummary(d))}</span>
          ${badge ? `<span class="dg-tile-badge">🕒 ${htmlEscape(badge)}</span>` : ''}
        </span>
        <span class="dg-tile-cta">Zusammenstellen →</span>
      </button>`;
  }).join('');
  return `<div class="dg-tiles">${tiles}</div>`;
}

/** Gemeinsamer Mount-Helper: hängt die Deal-Kacheln in den `.dg-host` INNERHALB
 *  des übergebenen Menü-Root-Elements (scoped — kein globales getElementById, daher
 *  funktioniert es in JEDEM der vier Menü-Mounts gleichzeitig). ctx.isOperator
 *  steuert die Sichtbarkeit (Gast: Fenster · Cockpit: immer). */
export async function mountDealTiles(rootEl, { isOperator } = {}) {
  if (!rootEl || typeof rootEl.querySelector !== 'function') return;
  _root = rootEl;
  _isOperator = !!isOperator;
  const host = rootEl.querySelector('.dg-host');
  if (!host) return;
  _deals = await loadVisibleDeals({ isOperator: _isOperator });
  // S171 P5-Folge — sicherstellen, dass alle vom Deal referenzierten Items im
  // Menü-Cache liegen (deal-only Mittags-Salat/-Bowl + Hausdressing fehlen, wenn
  // der Cache vor der phase_130-Migration geladen wurde). Sonst rendern Salat/
  // Bowl-Gruppen leer und das Picker-Anpassen-Modal findet das Gericht nicht.
  await _ensureDealItemsCached(_deals);
  // Root kann durch zwischenzeitliches Re-Render ersetzt sein → Host frisch holen.
  const h2 = _root && _root.querySelector('.dg-host');
  if (h2) h2.innerHTML = renderDealTilesHtml(_deals);
}

/** Lädt den Menü-Cache komplett neu, falls ein vom Deal referenziertes menu_item
 *  (Gruppen-/Add-on-Item) noch nicht im Cache ist. Ein einziger Reload deckt auch
 *  Topping-/Dressing-Optionen (z. B. Hausdressing) ab, die nicht per id im Deal stehen. */
async function _ensureDealItemsCached(deals) {
  try {
    const ids = new Set();
    (deals || []).forEach(d => {
      (d.groups || []).forEach(g => (g.items || []).forEach(it => it && it.menu_item_id && ids.add(it.menu_item_id)));
      (d.addons || []).forEach(a => (a.items || []).forEach(it => it && it.menu_item_id && ids.add(it.menu_item_id)));
    });
    if (!ids.size) return;
    const have = new Set(_menu().map(m => m.id));
    const missing = [...ids].some(id => !have.has(id));
    if (!missing) return;
    const st = window.App && window.App.storage;
    if (st && st.menu && typeof st.menu.reload === 'function') await st.menu.reload();
  } catch (e) { console.warn('[deals-guest] ensure menu cache:', e && e.message); }
}

/* -------------------------------------------------------------------------- */
/* Geführtes Modal                                                             */
/* -------------------------------------------------------------------------- */

export function dealsGuestOpen(dealId) {
  const deal = _deals.find(d => d.id === dealId);
  if (!deal) { showToast('Deal nicht verfügbar.', 'error'); return; }
  _sel = { deal };
  _renderModal();
}

export function dealsGuestClose() {
  _sel = null;
  const ov = (_root && _root.querySelector('#dg-modal-overlay')) || document.getElementById('dg-modal-overlay');
  if (ov) ov.remove();
  // Body-Scroll wird vom Picker verwaltet; hier nur sicherheitshalber lösen.
  if (!document.querySelector('.mp-modal-overlay')) document.body.style.overflow = '';
}

function _renderModal() {
  const root = _root;
  if (!root || !_sel) return;
  let ov = root.querySelector('#dg-modal-overlay') || document.getElementById('dg-modal-overlay');
  const html = _modalHtml();
  if (!ov) {
    root.insertAdjacentHTML('beforeend', html);
    document.body.style.overflow = 'hidden';
  } else {
    ov.outerHTML = html;
  }
}

/** Dish-Auswahl wie die NORMALE Karte: je deal_group ein Abschnitt mit den
 *  zugeordneten menu_items als gewohnte Menü-Kacheln (mp-browse-card). Klick →
 *  gewohntes Anpassen-Modal des Pickers (mpOpenDealDraft) im Deal-Kontext. */
function _modalHtml() {
  const deal = _sel.deal;
  const groupsHtml = (deal.groups || []).map(grp => {
    const price = fmtEUR(Number(grp.deal_price_cents) / 100);
    const cards = (grp.items || []).map(it => {
      const m = _mi(it.menu_item_id);
      if (!m) return '';
      return `
        <button type="button" class="mp-browse-card dg-dish-card" onclick="dealsGuestPickDish('${htmlEscape(grp.id)}','${htmlEscape(it.menu_item_id)}')" title="Zusammenstellen">
          <div class="mp-card-name">${htmlEscape(m.name)}</div>
          <div class="mp-card-meta"><span class="mp-card-price">${price}</span></div>
          ${m.description ? `<div class="mp-card-desc">${htmlEscape(m.description)}</div>` : ''}
        </button>`;
    }).join('');
    if (!cards) return '';
    return `
      <div class="dg-dish-group">
        <div class="dg-dish-group-head">${htmlEscape(grp.name)} <span class="dg-grp-price">${price}</span></div>
        <div class="mp-browse-grid">${cards}</div>
      </div>`;
  }).join('');

  return `
    <div class="dg-modal-overlay" id="dg-modal-overlay" onclick="if(event.target===this)dealsGuestClose()">
      <div class="dg-modal">
        <div class="dg-modal-head">
          <h2>${htmlEscape(deal.name)} <span class="dg-hint">— Gericht wählen</span></h2>
          <button type="button" class="dg-close" onclick="dealsGuestClose()" aria-label="Schließen">×</button>
        </div>
        <div class="dg-modal-body">
          ${groupsHtml || '<p class="dg-hint">Keine Gerichte hinterlegt.</p>'}
        </div>
      </div>
    </div>`;
}

/** Gericht gewählt → Deal-Auswahl-Modal schließen, gewohntes Anpassen-Modal des
 *  Pickers im Deal-Kontext öffnen (Preis = Gruppen-Deal-Preis, + Beilage/Getränk). */
export function dealsGuestPickDish(groupId, itemId) {
  if (!_sel || !_sel.deal) return;
  const deal = _sel.deal;
  const ctx = { dealDetail: deal, dealName: deal.name, groupId, lineUid: uid('dl') };
  dealsGuestClose();
  if (typeof window.mpOpenDealDraft === 'function') {
    window.mpOpenDealDraft(itemId, ctx);
  } else {
    console.warn('[deals-guest] mpOpenDealDraft fehlt (Picker nicht geladen?)');
    showToast('Konnte nicht geöffnet werden.', 'error');
  }
}

/* Test-Hook */
export function _setDealsForTest(deals) { _deals = Array.isArray(deals) ? deals : []; }

/* -------------------------------------------------------------------------- */
/* window-Bridge (CSP: func())                                                 */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    mountDealTiles, dealsGuestOpen, dealsGuestClose, dealsGuestPickDish
  });
}
